'use client';

import { ClockUserIcon, FolderOpenIcon } from "@phosphor-icons/react";
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { captureError } from '@/shared/functions';
import { Popover } from '@tranquility-ai/theme-components/popover';
import '@tranquility-ai/theme-components/popover.css';
import { useReactiveAuthTokens, useReactiveFeatureFlags } from '@/app/contexts';
import { formatTime } from '@/shared/clientFunctions';
import Backlog, { BacklogFromApi } from "./ui/backlog";
import { recordStat } from '@/shared/functions';
import { StatsContext } from '@/app/contexts';
import { Button } from '@tranquility-ai/theme-components/button';
import { InternalTools } from './internal-tools';
import { PrototypeMenu } from './prototypes/PrototypeMenu';
import EmployeeSwitcher from './EmployeeSwitcher';
import { FeedbackAnalyticsPopover } from './feedback-analytics-popover';
import {
  statusBarIconButtonClassName,
  statusBarIconButtonProps,
} from './status-bar-icon-button';
import { RemoteStorage } from "@/shared/types";
import { fetchProvenanceStatusCounts } from '@/shared/cors-provenance-api';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';

export interface StatusBarProps {
  status: string;
  userId: string;
  timeSaved: number;
  loadingTimeSaved: boolean;
  caseId: string | undefined;
  remoteStorageLink: RemoteStorage | null;
  onOpenCaseFileManagement?: () => void;
  isCaseFileManagementView?: boolean;
  /** Same gate as Account-view Case Management — permission-service admin + caseManagementDashboard. */
  showEmployeeSwitcher?: boolean;
}

const StatusBarComponent: React.FC<StatusBarProps> = ({
  status,
  userId,
  timeSaved,
  loadingTimeSaved,
  caseId,
  remoteStorageLink,
  onOpenCaseFileManagement,
  isCaseFileManagementView = false,
  showEmployeeSwitcher = false,
}) => {
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [backlogData, setBacklogData] = useState<BacklogFromApi>();
  const [provenanceStatusCounts, setProvenanceStatusCounts] = useState<{ [key: string]: number }>({});
  const [timeSavedLocal, setTimeSavedLocal] = useState(timeSaved);

  const featureFlags = useReactiveFeatureFlags();
  const statsContext = useContext(StatsContext);
  const { idToken, accessToken } = useReactiveAuthTokens();
  const { cellId, accessToken: realAccessToken, enabled: cellSessionEnabled } = useCellServiceSession();

  useEffect(() => {
    setTimeSavedLocal(timeSaved);
  }, [timeSaved]);

  const fetchBacklog = useCallback(async () => {
    // TODO: restore body below once the backlog backend service is available behind API Gateway
    /* BEGIN RESTORE
    try {
      const response = await FetchWithVersionCheck('/api/protected/backlog', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        }
      });
      const data = await response.json();
      if (!response.ok || !data) {
        const errorMessage = `Error fetching sqs/backlog info`;
        captureError(errorMessage, undefined);
      }
      if (data && data.backlog) {
        setBacklogData(data.backlog);
      }
    } catch (error) {
      const errorMessage = `Error fetching sqs/backlog info: ${error}`;
      captureError(errorMessage, error as Error);
    }
    END RESTORE */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (featureFlags?.backlog && idToken && accessToken) {
      fetchBacklog();
    }
  }, [fetchBacklog, featureFlags?.backlog, idToken, accessToken]);

  const fetchProvenaceData = useCallback(async () => {
    if (!cellId || !realAccessToken) return;
    try {
      const data = await fetchProvenanceStatusCounts({
        cellId,
        accessToken: realAccessToken,
      });
      if (data?.responses?.[0]?.counts) {
        setProvenanceStatusCounts(data.responses[0].counts as { [key: string]: number });
      }
    } catch (error) {
      const errorMessage = `Error fetching provenance service status counts: ${error}`;
      captureError(errorMessage, error as Error);
    }
  }, [cellId, realAccessToken]);

  useEffect(() => {
    if (featureFlags?.backlog && cellSessionEnabled) {
      fetchProvenaceData();
    }
  }, [fetchProvenaceData, featureFlags?.backlog, cellSessionEnabled]);

  const isFooter = !!featureFlags?.enableMediaAndSourceShare;

  return (
    <div className={`box-border w-full hidden sm:flex fixed bg-[#060F1B] flex-row items-center py-1 px-3 sm:px-4 gap-2 text-center text-3xs text-gray-100 font-inter min-h-[39px] ${
      isFooter
        ? 'bottom-0 left-0 right-0 justify-start border-t border-white/[0.06] z-40'
        : 'top-[72px] left-0 justify-center border-b border-white/[0.06]'
    }`}>
      {(!!featureFlags?.['timeSaved'] || !!featureFlags?.['newTimeSaved']) && (
        <div className="flex flex-row gap-1.5 items-center justify-center text-sm opacity-60 hover:opacity-100 whitespace-nowrap">
          <ClockUserIcon size={22} weight="duotone" />
        {loadingTimeSaved ? (
          <div className="animate-pulse">Calculating time savings with TimePilot...</div>
        ) : (
          <Popover
            id="TimeSavingsPopoverRead"
            isOpen={isPopoverVisible}
            onOpenChange={(open) => {
              setIsPopoverVisible(open);
              if (open) {
                recordStat(
                  'TimeSavingsPopoverReadHover',
                  new MouseEvent('hover') as unknown as React.MouseEvent,
                  statsContext                );
              }
            }}
            placement={isFooter ? "top-start" : "bottom-start"}
            maxWidth="21.875rem"
            content={
              <div className="flex flex-col gap-4">
                <div className="flex flex-row gap-4">
                  <div className="">
                    <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" fill="#2d8dfe" viewBox="0 0 256 256" stroke="#2d8dfe">
                      <path d="M136,72v43.05l36.42-18.21a8,8,0,0,1,7.16,14.31l-48,24A8,8,0,0,1,120,128V72a8,8,0,0,1,16,0Zm-8,144a88,88,0,1,1,88-88,8,8,0,0,0,16,0A104,104,0,1,0,128,232a8,8,0,0,0,0-16Zm103.73,5.94a8,8,0,1,1-15.46,4.11C213.44,215.42,203.46,208,192,208s-21.44,7.42-24.27,18.05A8,8,0,0,1,160,232a8.15,8.15,0,0,1-2.06-.27,8,8,0,0,1-5.67-9.79,40,40,0,0,1,17.11-23.32,32,32,0,1,1,45.23,0A40,40,0,0,1,231.73,221.94ZM176,176a16,16,0,1,0,16-16A16,16,0,0,0,176,176Z"></path>
                    </svg>
                  </div>
                  <p className="m-0 p-0 text-[16px] italic leading-normal text-[var(--tp-popover-fg)]">
                    Your team has saved <span className="font-semibold text-[18px] text-brand-blue">{formatTime(timeSavedLocal)}</span> investigating dozens of cases with TimePilot.</p>
                </div>
              </div>
            }
          >
            <Button
              type="button"
              style="text"
              color="light"
              background="inverse"
              data-testid="TimeSavingsPopover"
              aria-label={`Time Savings: ${formatTime(timeSavedLocal, true)} (${formatTime(timeSavedLocal, false, true)})`}
              className="flex flex-row items-center justify-center gap-1 hover:cursor-help bg-transparent border-none p-0"
              onMouseEnter={(event) => recordStat(
                'TimeSavingsPopoverClicked',
                event,
                statsContext              )}
              onClick={() => setIsPopoverVisible((current) => !current)}
            >
              Time Savings: <span className="italic text-[15px] pr-[3px] pt-[.5px] opacity-100">{formatTime(timeSavedLocal, true)} ({formatTime(timeSavedLocal, false, true)})</span>
            </Button>
          </Popover>
        )}
      </div>)}
      {!isFooter && <div className="flex-1 relative h-[15px] overflow-hidden" />}
      <Backlog
        backlogData={backlogData as BacklogFromApi}
        provenanceStatusCounts={provenanceStatusCounts}
        userId={userId}
        caseId={caseId}
        remoteStorageLink={remoteStorageLink}
      />
      {showEmployeeSwitcher && <EmployeeSwitcher />}
      {!!featureFlags?.feedbackSection && (
        <FeedbackAnalyticsPopover placement={isFooter ? 'top-start' : 'bottom-end'} />
      )}
      {!!featureFlags?.caseFileManagement && onOpenCaseFileManagement && (
        // AGENT: do not restyle this trigger. Canonical circular inverse
        // icon button — no className/prop overrides, no duotone.
        <Button
          type="button"
          {...statusBarIconButtonProps}
          aria-label="Case Files"
          aria-pressed={isCaseFileManagementView}
          className={statusBarIconButtonClassName}
          onClick={onOpenCaseFileManagement}
        >
          <FolderOpenIcon size={22} weight="regular" />
        </Button>
      )}
      <PrototypeMenu caseId={caseId} />
      {featureFlags?.powerUserOrg && <InternalTools />}
    </div>
  );
};

/**
 * Memoized so unrelated `Main` renders don't cascade into the bar. Flags and
 * auth are tracked via reactive hooks, so the bar still updates when either
 * data source changes.
 */
const StatusBar = React.memo(StatusBarComponent);

export default StatusBar;
